import { useCallback, useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  GoogleAuthProvider,
  isSignInWithEmailLink,
  sendSignInLinkToEmail,
  signInWithEmailLink,
  signInWithPopup,
} from 'firebase/auth';
import { apiClient, ACTIVE_ORG_STORAGE_KEY } from '@/core/api';
import { auth } from '@/core/firebase';
import AuthLayout from '@/layouts/AuthLayout';
import { useAuth } from '@/contexts/AuthContext';
import type { MeResponse } from '@/types/api';

const EMAIL_LINK_KEY = 'aspectly_email_for_signin';

function resolveActiveOrgId(me: MeResponse): number | null {
  const fromUser = me.user.organization_id ?? me.user.organization_ids?.[0];
  if (fromUser != null) return fromUser;
  return me.organizations.find((org) => org.id != null)?.id ?? null;
}

export default function LoginPage() {
  const [email, setEmail] = useState('');
  const [emailSending, setEmailSending] = useState(false);
  const [emailSent, setEmailSent] = useState(false);
  const [loading, setLoading] = useState(false);
  const [verifying, setVerifying] = useState(false);
  const [error, setError] = useState('');
  const { isAuthenticated, loading: authLoading } = useAuth();
  const navigate = useNavigate();

  const verifyRegistrationAndRoute = useCallback(async () => {
    if (!auth.currentUser) return;
    setVerifying(true);
    setError('');
    try {
      const me = await apiClient<MeResponse>('/users/me');
      const activeOrgId = resolveActiveOrgId(me);
      if (activeOrgId != null) {
        window.localStorage.setItem(ACTIVE_ORG_STORAGE_KEY, String(activeOrgId));
        navigate('/', { replace: true });
        return;
      }
      navigate('/setup', { replace: true });
    } catch (err: unknown) {
      const message = err instanceof Error ? err.message : 'Verification failed';
      if (message.includes('403') || message.includes('User not registered')) {
        navigate('/setup', { replace: true });
        return;
      }
      setError(message);
    } finally {
      setVerifying(false);
    }
  }, [navigate]);

  useEffect(() => {
    if (authLoading || !isAuthenticated) return;
    void verifyRegistrationAndRoute();
  }, [authLoading, isAuthenticated, verifyRegistrationAndRoute]);

  useEffect(() => {
    if (!isSignInWithEmailLink(auth, window.location.href)) return;
    const storedEmail = window.localStorage.getItem(EMAIL_LINK_KEY);
    const confirmedEmail = storedEmail ?? window.prompt('Confirm your email for sign-in');
    if (!confirmedEmail) {
      setError('Email address is required to complete sign-in.');
      return;
    }

    setEmail(confirmedEmail);
    setError('');
    setEmailSent(false);
    setEmailSending(true);
    signInWithEmailLink(auth, confirmedEmail, window.location.href)
      .then(() => {
        window.localStorage.removeItem(EMAIL_LINK_KEY);
      })
      .catch((err) => {
        setError(err instanceof Error ? err.message : 'Email sign-in failed');
      })
      .finally(() => {
        setEmailSending(false);
      });
  }, []);

  async function handleEmailLogin(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    if (loading || verifying || emailSending) {
      setError('Please wait for the current sign-in to finish.');
      return;
    }
    const normalizedEmail = email.trim();
    if (!normalizedEmail) {
      setError('Please enter an email address.');
      return;
    }

    setError('');
    setEmailSent(false);
    setEmailSending(true);
    try {
      await sendSignInLinkToEmail(auth, normalizedEmail, {
        url: `${window.location.origin}/login`,
        handleCodeInApp: true,
      });
      window.localStorage.setItem(EMAIL_LINK_KEY, normalizedEmail);
      setEmailSent(true);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to send email');
    } finally {
      setEmailSending(false);
    }
  }

  async function handleGoogleLogin() {
    if (verifying || emailSending) return;
    setLoading(true);
    setError('');
    try {
      await signInWithPopup(auth, new GoogleAuthProvider());
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
    } finally {
      setLoading(false);
    }
  }

  return (
    <AuthLayout>
      <div className="w-full max-w-md bg-surface-container-lowest p-10 rounded-xl ambient-shadow relative overflow-hidden -translate-y-8">
        {/* Top accent gradient */}
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-primary to-primary-dim" />

        <div className="text-center mb-8">
          <h1 className="font-headline text-3xl font-extrabold tracking-tight text-on-surface mb-2">
            Welcome Back
          </h1>
          <p className="text-on-surface-variant text-sm">
            Enter your email to receive a magic link to your workspace.
          </p>
        </div>

        {/* Error */}
        {error && (
          <div className="mb-4 p-3 rounded-lg bg-error-container text-on-error-container text-sm">
            {error}
          </div>
        )}

        {emailSent && (
          <div className="mb-4 p-3 rounded-lg bg-primary-container text-primary text-sm">
            Magic link sent. Check your inbox to finish signing in.
          </div>
        )}

        {/* Google login */}
        <div className="space-y-3 mb-8">
          <button
            onClick={handleGoogleLogin}
            disabled={loading || verifying || emailSending}
            className="w-full flex items-center justify-center gap-3 py-3 px-4 bg-surface-container-low hover:bg-surface-container transition-colors rounded-xl text-sm font-medium text-on-surface-variant border border-outline-variant/10 cursor-pointer disabled:opacity-60"
          >
            <svg className="w-5 h-5" viewBox="0 0 24 24">
              <path
                fill="#4285F4"
                d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 0 1-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z"
              />
              <path
                fill="#34A853"
                d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
              />
              <path
                fill="#FBBC05"
                d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18A11.96 11.96 0 0 0 1 12c0 1.94.46 3.77 1.18 5.42l3.66-2.84z"
              />
              <path
                fill="#EA4335"
                d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
              />
            </svg>
            {verifying ? 'Verifying workspace…' : loading ? 'Signing in…' : 'Continue with Google'}
          </button>
        </div>

        {/* Divider */}
        <div className="flex items-center gap-4 mb-8">
          <div className="h-px flex-grow bg-outline-variant/20" />
          <span className="text-[10px] font-bold tracking-widest text-on-surface-variant/60 uppercase">
            Or Email Login
          </span>
          <div className="h-px flex-grow bg-outline-variant/20" />
        </div>

        {/* Email form */}
        <form className="space-y-6" onSubmit={handleEmailLogin}>
          <div className="space-y-2">
            <label className="block text-xs font-semibold tracking-wider text-on-surface-variant uppercase ml-1">
              Email Address
            </label>
            <div className="relative group">
              <span className="material-symbols-outlined absolute left-4 top-1/2 -translate-y-1/2 text-on-surface-variant/50 group-focus-within:text-primary transition-colors">
                alternate_email
              </span>
              <input
                className="w-full pl-11 pr-4 py-3 bg-surface-container-low border-b-2 border-transparent focus:border-primary focus:ring-0 transition-all rounded-t-lg text-sm text-on-surface outline-none placeholder:text-outline-variant"
                placeholder="name@company.com"
                type="email"
                value={email}
                onChange={(e) => {
                  setEmail(e.target.value);
                  if (emailSent) setEmailSent(false);
                }}
              />
            </div>
          </div>
          <button
            type="submit"
            disabled={loading || verifying || emailSending}
            className="w-full py-4 bg-gradient-to-br from-primary to-primary-dim text-on-primary font-headline font-bold rounded-xl shadow-lg shadow-primary/20 hover:opacity-90 active:scale-[0.98] transition-all tracking-wide cursor-pointer disabled:opacity-60"
          >
            {emailSending ? 'Sending link…' : 'Login'}
          </button>
        </form>

        <div className="mt-8 text-center">
          <p className="text-sm text-on-surface-variant">
            Don&apos;t have an account?{' '}
            <a className="text-primary font-semibold hover:underline" href="#">
              Signup today!
            </a>
          </p>
        </div>
      </div>
    </AuthLayout>
  );
}
